import {
  prop,
  keys,
  endsWith,
  propOr,
  equals,
  includes,
  path,
  pipe,
  reject,
  length,
  mapObjIndexed,
  last
} from 'ramda'

import {
  setResendMessage,
  showPasswordlessLogin,
  INIT_STATUS,
  StateType
} from './slice'

import {
  sendMessage,
  makeOtpLogin,
  answerCustomChallenge,
  startLoginSignUp,
  updateUserAttributes,
  uploadUserPicture
} from './thunks'

import {AppMetaData, ClaimsType, AuthenticationFlows} from '../types/Auth'

import {
  CognitoSession,
  User as CognitoUser,
  UserAttributes,
  CustomUserData
} from '../types/Cognito'

import {
  MessageAttemptsHistoryType,
  MessageChannelsType,
  ChannelsType
} from '../types/Passwordless'

import {User} from '@auth0/auth0-spa-js'
import {PLUS_TIERS, USER_TIERS} from '../../offer/business/privateDeals'
import {EnabledBrands} from '../../brand/types'
import {createSelector} from '@reduxjs/toolkit'

const AUTHENTICATION_PATH = 'authentication'

export type AuthenticationStateType = {
  authentication: StateType
}

/**
 * Returns the stored user id
 */
export const getUserId: (arg0: ClaimsType) => string = prop('sub')

/**
 * Selector that returns the user's phone number in case there's one registered
 * @param state - the redux state holding the authentication info
 */
export const getUserPhoneNumber = (state: AuthenticationStateType) =>
  state[AUTHENTICATION_PATH].userPhoneNumber

/**
 * Selector that returns the user's authentication status
 * @param state - the redux state holding the authentication info
 */
const getAuthenticationStatus = (
  state: AuthenticationStateType
): string | null | undefined => state[AUTHENTICATION_PATH].status

/**
 * Selector that returns the how many times the user has logged in
 * @param state - the redux state holding the authentication info
 * @internal
 */
export const getLoginCountKey = (
  claims: ClaimsType
): string | null | undefined => keys(claims).find(endsWith('logins_count'))

/**
 * Selector that returns the how many times the user has logged in
 * @param state - the redux state holding the authentication info
 */
export const getLoginCount = (claims: ClaimsType): number =>
  propOr(0, getLoginCountKey(claims))(claims)

/**
 * returns the `app_metadata` in the `claims`
 * @param user Auth0 user
 */
export const getAppMetaData = (user: User): AppMetaData => {
  const key = keys(user).find(endsWith('app_metadata'))
  const appMetaData = user[key] as AppMetaData
  if (appMetaData) return appMetaData
}

/**
 * Get the current user tier.
 * Different tiers can be offered different rates and experiences
 * @param user - Auth0 user
 * @returns ['employee', 'plus']
 */
export const getUserTier = (user: User): USER_TIERS[] | [] =>
  getAppMetaData(user)?.tier || []

/**
 * Returns whether the user is member plus or not
 * @param user Cognito user
 */
export const getCognitoIsMemberPlus = (user: CognitoUser): boolean =>
  hasPlusTier(getCognitoUserTier(user)) || false

/**
 * Checks whether the user has any plus tier (is a plus member or not)
 * @param userTier The user tier values from Auth0
 * @returns boolean
 */
export const hasPlusTier = (userTier: USER_TIERS[] | []): boolean =>
  userTier.some(tier => PLUS_TIERS.includes(tier))

/**
 * Get the current user lastCheckoutDate.
 * Used to check if member plus is still valid and other conditionals.
 * @param user - Auth0 user
 * @returns "2022-12-29T00:00:00.000Z"
 */
export const getUserLastCheckoutDate = (user: User): string =>
  getAppMetaData(user)?.lastCheckoutDate || ''

/**
 * Selector that returns whether the UI visibility state of the passwordless login overlay
 * @param state - the redux state holding the authentication info
 */
export const isPasswordlessLoginVisible = (
  state: AuthenticationStateType
): boolean => state.authentication?.isPasswordlessLoginVisible

/**
 * Selector that returns if login attempt for passwordless authentication was rejected
 * @param state - the redux state holding the authentication info
 */
export const hasPasswordlessLoginError = (
  state: AuthenticationStateType
): boolean =>
  equals(makeOtpLogin.rejected.type, getAuthenticationStatus(state)) ||
  equals(startLoginSignUp.rejected.type, getAuthenticationStatus(state)) ||
  equals(answerCustomChallenge.rejected.type, getAuthenticationStatus(state))

export const isGettingUserPhoneNumber = (
  state: AuthenticationStateType
): boolean =>
  includes(getAuthenticationStatus(state), [
    sendMessage.rejected.type,
    showPasswordlessLogin.type,
    setResendMessage.type,
    INIT_STATUS
  ])

export const hasAuth0SendErrorStatus = (
  state: AuthenticationStateType
): boolean =>
  equals(sendMessage.rejected.type, getAuthenticationStatus(state)) ||
  equals(startLoginSignUp.rejected.type, getAuthenticationStatus(state))

/**
 * Selector that returns if the phone number provided by the user encountered a validation error
 * provided by Auth0
 * @param state - the redux state holding the authentication info
 */
export const hasAuth0PhoneNumberValidationError = (
  state: AuthenticationStateType
): boolean => {
  const isPhoneNumberValidationError = includes(
    path([AUTHENTICATION_PATH, 'error', 'errorType'], state),
    ['bad.phone_number', 'sms_provider_error']
  )

  return Boolean(hasAuth0SendErrorStatus && isPhoneNumberValidationError)
}

export const hasAuth0GenericErrorResponse = (
  state: AuthenticationStateType
): boolean =>
  hasAuth0SendErrorStatus(state) && !hasAuth0PhoneNumberValidationError(state)

/**
 * Selects in the redux store the channel the user used to authenticate on the session
 * @param state The redux state
 * @returns the channel an user used to authenticate
 */
export const getSelectedChannel = (
  state: AuthenticationStateType
): MessageChannelsType | null | undefined =>
  state[AUTHENTICATION_PATH].selectedChannel

/**
 * Selector that returns which were the channels that the user used to login
 * The available message types are: {@link MessageChannelsType}
 * @param state - the redux state holding the authentication info
 */
export const getMessagesAttemptsHistory = (
  state: AuthenticationStateType
): MessageAttemptsHistoryType => state[AUTHENTICATION_PATH].attemptsHistory

/**
 * Selector that returns if the user is trying to resend an activation code
 * @param state - the redux state holding the authentication info
 */
export const getIsResendingPasswordlessMessage = (
  state: AuthenticationStateType
): boolean => state[AUTHENTICATION_PATH].isUserResending

/**
 * Selector that returns a given property named by `channelProp`, from all channel objects.
 * @param channelProp - the name of the property inside a channel you want to retrieve the information from. The keys of {@link ChannelsType}
 * @param channel - the channels we have available for login
 */
export const getChannelProp = (
  channelProp: string,
  channels: ChannelsType
): Record<MessageChannelsType, any> =>
  mapObjIndexed(prop(channelProp), channels)

/**
 * Returns how many login attempts were made from all channels
 * @param state - the redux state holding the authentication info
 */
export const getChannelsAttempts = (
  state: AuthenticationStateType
): Record<MessageChannelsType, number> =>
  getChannelProp('attempts', state[AUTHENTICATION_PATH].channels)

/**
 * Returns which of the available {@link MessageChannelsType | channels} is enabled
 * @param state - the redux state holding the authentication info
 */
export const getEnabledChannels = (
  state: AuthenticationStateType
): Record<MessageChannelsType, boolean> =>
  getChannelProp('enabled', state[AUTHENTICATION_PATH].channels)

/**
 * Checks in the state if there's any authentication channel available for use
 * @param state The redux state
 * @returns if there's any channel available
 */
export const getEnabledChannelAvailable = (state: AuthenticationStateType) =>
  pipe(
    reject(channel => !channel),
    keys,
    length
  )(getEnabledChannels(state)) > 0

/**
 * Returns which {@link AuthenticationFlows | authentication flow} the user is trying to use
 * @param state - the redux state holding the authentication info
 */
export const getAuthenticationFlow = (
  state: AuthenticationStateType
): AuthenticationFlows | null => state[AUTHENTICATION_PATH].flow

/**
 * Selector that returns if the user is privileged
 * @param state - the redux state holding the authentication info
 */
export const getIsPrivileged = (state: AuthenticationStateType): boolean =>
  state[AUTHENTICATION_PATH].isPrivileged

/**
 * Selector that returns if the user is authenticated
 * @param state - the redux state holding the authentication info
 */
export const getIsAuthenticated = (state: AuthenticationStateType): boolean =>
  state[AUTHENTICATION_PATH].isAuthenticated

export const selectIsAuthenticated = createSelector(
  getIsAuthenticated,
  isAuthenticated => isAuthenticated
)

/**
 * Selector that returns if it has the audience token
 * @param state - the redux state holding the authentication info
 */
export const getIsAudience = (state: AuthenticationStateType): boolean =>
  state[AUTHENTICATION_PATH].isAudience

/**
 * Selector that returns the user
 * @param state - the redux state holding the authentication info
 */
export const getUser = (state: AuthenticationStateType): CognitoUser =>
  state[AUTHENTICATION_PATH].user

/**
 * Selector that returns the loading state
 * @param state - the redux state holding the authentication info
 */
export const getIsLoading = (state: AuthenticationStateType): boolean =>
  state[AUTHENTICATION_PATH].isLoading

export const selectIsLoading = createSelector(
  getIsLoading,
  isLoading => isLoading
)

/**
 * Selector that returns the URL path name
 * @param state - the redux state holding the authentication info
 */
export const getLoginPathName = (state: AuthenticationStateType): string =>
  state[AUTHENTICATION_PATH].loginPathName

export const selectLoginPathName = createSelector(
  getLoginPathName,
  loginPathName => loginPathName
)

/**
 * Selector that returns the cognito user
 * @param state - the redux state holding the authentication info
 */
export const getCognitoSession = (
  state: AuthenticationStateType
): CognitoSession => state[AUTHENTICATION_PATH].cognitoSession

const getCognitoCustomUserData = (user: CognitoUser): CustomUserData =>
  JSON.parse(user?.attributes?.['custom:app_data'] || '{}')

export const getUserLegacyId = (user: CognitoUser) => {
  const customAppData = getCognitoCustomUserData(user)
  return customAppData?.auth0_id || null
}

/**
 * Get the current Cognito user tier.
 * Different tiers can be offered different rates and experiences
 * @param user - Cognito user
 * @returns ['employee', 'plus']
 */
export const getCognitoUserTier = (user: CognitoUser) =>
  getCognitoCustomUserData(user)?.tier || []

/**
 * Get Cognito User Id
 */
export const getCognitoUserId = (user: CognitoUser) => {
  return user?.attributes?.sub
}

/**
 * Get Cognito User Id
 */
export const getCognitoLoginCount = (user: CognitoUser) =>
  getCognitoCustomUserData(user)?.loginCount

export const getCognitoHasPlusTier = (userTier: USER_TIERS[] | []): boolean =>
  userTier.some(tier => PLUS_TIERS.includes(tier))

/**
 * Selector that returns the cognito user
 * @param state - the redux state holding the authentication info
 */
export const getCognitoUserAttributes = (
  state: AuthenticationStateType
): UserAttributes => state[AUTHENTICATION_PATH]?.user?.attributes

/**
 * In order to show correct success or error message in the account update form, we keep track of the status of the action that was performed.
 * This is related to the action which is performed by Cognito.
 * @param state - the redux state holding the authentication info
 */
export const getLastAction = (state: AuthenticationStateType): string =>
  state[AUTHENTICATION_PATH]?.lastAction

export const getUserProfileUpdatePending = (
  state: AuthenticationStateType
): boolean => getLastAction(state) === updateUserAttributes.pending.type

export const getUserProfilePictureUpdatePending = (
  state: AuthenticationStateType
): boolean => getLastAction(state) === uploadUserPicture.pending.type

/**
 * Selector that returns the user profile picture URL from the state
 * @param state - the redux state holding the authentication info
 */
export const getUserProfilePicture = (
  state: AuthenticationStateType
): string | null => state[AUTHENTICATION_PATH].profilePictureUrl

export const getProfileUpdateAlertType = (state: AuthenticationStateType) => {
  const actions = [
    updateUserAttributes.rejected.type,
    updateUserAttributes.fulfilled.type
  ]
  const lastAction = getLastAction(state)
  return actions.includes(lastAction) ? last(lastAction.split('/')) : ''
}

export const getEnabledBrands = (
  state: AuthenticationStateType
): EnabledBrands[] => state[AUTHENTICATION_PATH].enabledBrands

export const selectEnabledBrands = createSelector(
  getEnabledBrands,
  enabledBrands => enabledBrands || []
)

export const selectUser = createSelector(getUser, user => user)

export const selectCognitoUserTier = createSelector(getUser, user =>
  getCognitoUserTier(user)
)

export const getTraitsUser = (user?: CognitoUser) => {
  const legacy_id = getUserLegacyId(user)
  const tier = getCognitoUserTier(user)

  return (
    user && {
      legacy_id,
      tier
    }
  )
}
